/**
 * BOGO — Deposit Option
 * ---------------------------------------------------------------------------
 * Companion asset for sections/bogo-deposit-option.liquid. Owns the whole
 * "reserve with a deposit" feature for the BOGO Builder step-4 summary.
 *
 * Contract with assets/bogo-builder.js:
 *   window.BogoDeposit.isActive()   -> true only when the block is shown AND
 *                                      the customer picked "deposit" AND a
 *                                      real selling plan id is known.
 *   window.BogoDeposit.getPlanId()  -> that plan id, else null.
 *
 * bogo-builder.js attaches the plan to the PAID bike line only, keyed by its
 * index in selectedBikes. Never the free bike: Shopify's Buy X Get Y requires
 * the "get Y" item to be a one-time purchase, so a plan on the free line can
 * void the BOGO discount and bill for both bikes.
 *
 * Every DOM touch here is defensive. If anything is missing the feature
 * no-ops and the page checks out exactly as it does without this file.
 */
(function () {
  'use strict';

  if (window.__bogoDepositLoaded) return;
  window.__bogoDepositLoaded = true;

  var MOUNT_ATTR = 'data-bogo-deposit-mounted';
  var DEPOSIT_MODE = 'deposit';
  var FULL_MODE = 'full';

  var root = null;
  var template = null;
  var cfg = null;
  var ctaBtn = null;
  var ctaOriginal = '';
  var observer = null;
  var frame = null;

  var mode = FULL_MODE;
  var eligible = false;
  var planId = null;
  var paidHandle = '';
  var preorderPresent = false; // a chosen bike is a pre-order, before plan/price gating

  var planResolved = {}; // handle -> id | null
  var planPending = {}; // handle -> true
  var titleByHandle = {}; // handle -> product title, for free-bike matching
  var logged = {};

  // ---------------------------------------------------------------- logging

  function warnOnce(key, message) {
    if (logged[key]) return;
    logged[key] = true;
    if (window.console && console.warn) {
      console.warn('[bogo-deposit] ' + message);
    }
  }

  function guard(fn) {
    try {
      return fn();
    } catch (err) {
      warnOnce('threw', 'suppressed error, feature disabled: ' + (err && err.message));
      teardown();
      return undefined;
    }
  }

  // ------------------------------------------------------------ formatting

  function parseMoney(text) {
    if (!text) return 0;
    var cleaned = String(text).replace(/[^0-9.\-]/g, '');
    var n = parseFloat(cleaned);
    return isFinite(n) ? n : 0;
  }

  function money(n) {
    var value = isFinite(n) ? n : 0;
    var hasCents = Math.round(value * 100) % 100 !== 0;
    return (
      '$' +
      value.toLocaleString('en-CA', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2
      })
    );
  }

  function textOf(id) {
    var el = document.getElementById(id);
    return el ? el.textContent.trim() : '';
  }

  function setText(id, value) {
    var el = document.getElementById(id);
    if (el && el.textContent !== value) el.textContent = value;
  }

  // ------------------------------------------------------------------ config

  function readConfig(el) {
    var amount = parseFloat(el.getAttribute('data-deposit-amount'));
    return {
      depositAmount: isFinite(amount) && amount > 0 ? amount : 500,
      inStockMarker: (el.getAttribute('data-in-stock-marker') || 'business day').toLowerCase(),
      preorderRule: el.getAttribute('data-preorder-rule') || 'either',
      fullCodePreorderOnly: el.getAttribute('data-full-code-preorder-only') !== 'false',
      planName: el.getAttribute('data-plan-name') || '',
      requirePriceGap: el.getAttribute('data-require-price-gap') !== 'false',
      ctaDeposit: el.getAttribute('data-cta-deposit') || 'Pay [[amount]] Deposit →',
      noteTemplate: el.getAttribute('data-note-template') || '',
      hint: el.getAttribute('data-hint') || '',
      hintAddons: el.getAttribute('data-hint-addons') || '',
      showBadge: el.getAttribute('data-show-badge') !== 'false',
      showInstock: el.getAttribute('data-show-instock') !== 'false',
      instockTitle: el.getAttribute('data-instock-title') || '',
      instockBody: el.getAttribute('data-instock-body') || '',
      badgeTitle: el.getAttribute('data-badge-title') || '',
      badgeBody: el.getAttribute('data-badge-body') || '',
      fullSaving: parseFloat(el.getAttribute('data-full-saving')) || 0,
      fullSavingText: el.getAttribute('data-full-saving-text') || '',
      discountCode: (el.getAttribute('data-full-discount-code') || '').trim(),
      depositCodes: parseCodeMap(el.getAttribute('data-deposit-codes'))
    };
  }

  /**
   * Parses the code map. Two line forms, both "key: CODE":
   *
   *   stunner-lite-3 | Matte Black: STNLT3BOGODEPMB   variant-specific
   *   swift-lite-step-thru-3: SWFTLT3BOGODEP          whole product
   *
   * Variant rules are sorted longest-fragment-first so the most specific
   * colour wins if two fragments could both match. Malformed lines are
   * skipped rather than guessed at.
   */
  function parseCodeMap(raw) {
    var byHandle = {};
    var byVariant = [];
    var lines = String(raw || '').split(/[\r\n]+/);

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var sep = line.indexOf(':');
      if (sep < 0) continue;

      var key = line.slice(0, sep).trim();
      var code = line.slice(sep + 1).trim();
      if (!key || !code) continue;

      var bar = key.indexOf('|');
      if (bar < 0) {
        byHandle[key.toLowerCase()] = code;
        continue;
      }

      // "handle | frag | frag" — every fragment must appear in the variant
      // title, so a rule can pin both size and colour.
      var parts = key.split('|');
      var handle = parts[0].trim().toLowerCase();
      var fragments = [];
      for (var f = 1; f < parts.length; f++) {
        var frag = parts[f].trim().toLowerCase();
        if (frag) fragments.push(frag);
      }
      if (handle && fragments.length) {
        byVariant.push({ handle: handle, fragments: fragments, code: code });
      }
    }

    // Most specific first: more fragments wins, then longer total match.
    byVariant.sort(function (a, b) {
      if (b.fragments.length !== a.fragments.length) return b.fragments.length - a.fragments.length;
      return b.fragments.join('').length - a.fragments.join('').length;
    });
    return { byHandle: byHandle, byVariant: byVariant };
  }

  // -------------------------------------------------------- pre-order check

  /**
   * The merchandising truth is the ETA string, not variant.available — several
   * of these products sell past zero inventory. An empty ETA is treated as
   * NOT a pre-order: at least one live variant renders data-availability="",
   * and guessing "pre-order" there would show a deposit we cannot honour.
   */
  function isPreorder(etaText) {
    var t = String(etaText || '').trim();
    if (!t) return false;
    return t.toLowerCase().indexOf(cfg.inStockMarker) === -1;
  }

  function readHandle(id) {
    var raw = textOf(id).replace(/^\s*Handle\s*:\s*/i, '').trim();
    if (!raw || raw.toUpperCase() === 'UNDEFINED') return '';
    return raw;
  }

  // ---------------------------------------------------------- plan lookup

  /**
   * Returns the plan id, null when the product has none, or undefined while
   * the lookup is still in flight (caller treats undefined as "not yet
   * eligible" and gets a re-evaluate when it lands).
   */
  function getPlan(handle) {
    if (!handle) return null;
    if (Object.prototype.hasOwnProperty.call(planResolved, handle)) return planResolved[handle];
    if (planPending[handle]) return undefined;
    if (typeof window.fetch !== 'function') {
      planResolved[handle] = null;
      return null;
    }

    planPending[handle] = true;
    window
      .fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }
      })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (product) {
        planResolved[handle] = pickPlanId(product);
        if (product && product.title) titleByHandle[handle] = String(product.title).trim();
      })
      .catch(function () {
        planResolved[handle] = null;
      })
      .then(function () {
        delete planPending[handle];
        schedule();
      });

    return undefined;
  }

  function pickPlanId(product) {
    var groups = (product && product.selling_plan_groups) || [];
    var wanted = cfg.planName.toLowerCase();
    var fallback = null;

    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      var plans = (group && group.selling_plans) || [];
      if (!plans.length || !plans[0] || typeof plans[0].id !== 'number') continue;
      if (fallback === null) fallback = plans[0].id;
      if (!wanted) return plans[0].id;
      if (String(group.name || '').toLowerCase().indexOf(wanted) !== -1) return plans[0].id;
    }

    // A named match was requested but not found — do not silently substitute
    // some other purchase option (a subscription would be a very bad guess).
    return wanted ? null : fallback;
  }

  /** Handles of the two chosen bikes, straight off the selected cards. */
  function selectedHandles() {
    var out = [];
    var cards = document.querySelectorAll('.bogo-bike-grid product-card.selected[data-product-handle]');
    for (var i = 0; i < cards.length; i++) {
      var h = (cards[i].getAttribute('data-product-handle') || '').trim().toLowerCase();
      if (h) out.push(h);
    }
    return out;
  }

  /**
   * The FREE bike's handle: whichever selection is not the paid one. The same
   * model chosen twice means both entries match, and that handle is both.
   */
  function freeHandle() {
    var sel = selectedHandles();
    if (sel.length < 2) return '';
    if (sel[0] === sel[1]) return sel[0];

    var paid = String(paidHandle || '').trim().toLowerCase();
    if (!paid) return '';
    for (var i = 0; i < sel.length; i++) {
      if (sel[i] !== paid) return sel[i];
    }
    return '';
  }

  /** Warm the title cache for mapped products, used only by the fallback
      path in freeBikeCode when the selected cards are not marked. */
  function primeCodeProducts() {
    if (!cfg) return;
    for (var handle in cfg.depositCodes.byHandle) {
      if (Object.prototype.hasOwnProperty.call(cfg.depositCodes.byHandle, handle)) getPlan(handle);
    }
  }

  /**
   * Which discount code zeroes the FREE bike.
   *
   * The code has to land on the free line — zeroing the free bike is its whole
   * job. Keying it off the PAID bike put the discount on the line carrying the
   * selling plan, which zeroed the dearer bike and charged the cheaper one.
   *
   * Primary lookup is the selected card's data-product-handle: synchronous,
   * exact, and available before the customer can click. Title matching is kept
   * only as a fallback for when the cards are not marked.
   */
  function freeBikeCode() {
    if (!cfg) return '';

    var handle = freeHandle();
    var variantTitle = textOf('checkout-bike2-variant').toLowerCase();

    // Most specific first: this product in this colour.
    if (handle && variantTitle) {
      for (var i = 0; i < cfg.depositCodes.byVariant.length; i++) {
        var rule = cfg.depositCodes.byVariant[i];
        if (rule.handle !== handle) continue;
        var all = true;
        for (var f = 0; f < rule.fragments.length; f++) {
          if (variantTitle.indexOf(rule.fragments[f]) === -1) { all = false; break; }
        }
        if (all) return rule.code;
      }
    }

    // Then a whole-product rule.
    if (handle && Object.prototype.hasOwnProperty.call(cfg.depositCodes.byHandle, handle)) {
      return cfg.depositCodes.byHandle[handle];
    }

    // Last resort, for when the selected cards carry no handle: match the
    // displayed product name against cached product titles.
    var shown = textOf('checkout-bike2-name');
    if (!shown) return '';
    for (var h in cfg.depositCodes.byHandle) {
      if (!Object.prototype.hasOwnProperty.call(cfg.depositCodes.byHandle, h)) continue;
      if (!Object.prototype.hasOwnProperty.call(titleByHandle, h)) { getPlan(h); continue; }
      if (titleByHandle[h].toLowerCase() === shown.toLowerCase()) return cfg.depositCodes.byHandle[h];
    }
    return '';
  }

  // -------------------------------------------------------------- evaluate

  function evaluate() {
    if (!root || !root.isConnected) return;

    var paidName = textOf('checkout-bike1-name');
    var freeName = textOf('checkout-bike2-name');
    var bothChosen = !!(paidName && freeName);

    var paidEta = textOf('checkout-bike1-shipping');
    var freeEta = textOf('checkout-bike2-shipping');
    var paidPre = isPreorder(paidEta);
    var freePre = isPreorder(freeEta);

    /**
     * Which bike's stock status makes the offer relevant. Default 'either':
     * the page promises the bikes ship together, so one backordered bike
     * means the customer is waiting on both and a deposit holds the pair.
     * 'paid' requires the dearer bike — the line that actually carries the
     * plan — to be the pre-order. 'both' only offers it when nothing in the
     * order could ship yet.
     */
    var anyPreorder =
      cfg.preorderRule === 'paid' ? paidPre :
      cfg.preorderRule === 'both' ? (paidPre && freePre) :
      (paidPre || freePre);

    var paidPrice = parseMoney(textOf('checkout-bike1-price'));
    var freePrice = parseMoney(textOf('checkout-bike2-original'));

    /**
     * When both bikes cost the same, which line the BOGO discount zeroes is
     * Shopify's choice, not ours. If it picks the line carrying the deposit
     * plan, Buy X Get Y's "the free item must be a one-time purchase" rule
     * can void the discount and bill for both bikes. Until that is verified
     * against a real cart, refuse the deposit rather than risk it.
     */
    var priceGapOk = !cfg.requirePriceGap || (paidPrice > 0 && paidPrice > freePrice);
    if (bothChosen && anyPreorder && !priceGapOk) {
      warnOnce('samepricehold', 'both bikes cost the same — deposit option held back (see requirePriceGap)');
    }

    // Tracked separately from `eligible`: the pay-in-full incentive keys off a
    // pre-order being in the order, not off whether a deposit was offerable.
    preorderPresent = bothChosen && anyPreorder;

    var canAsk = bothChosen && anyPreorder && priceGapOk;
    paidHandle = readHandle('checkout-bike1-handle');
    var plan = canAsk ? getPlan(paidHandle) : null;
    planId = typeof plan === 'number' ? plan : null;

    eligible = canAsk && planId !== null;

    // A stale deposit choice must never survive into an all-in-stock pair.
    if (!eligible && mode === DEPOSIT_MODE) mode = FULL_MODE;

    render({
      paidName: paidName,
      paidEta: paidEta,
      freeName: freeName,
      freeEta: freeEta,
      paidPrice: paidPrice,
      total: parseMoney(textOf('checkout-total'))
    });
  }

  function render(view) {
    root.hidden = !eligible;

    var isDeposit = eligible && mode === DEPOSIT_MODE;

    // Add-ons are derived from the displayed figures rather than summed from
    // the optional rows, so this stays correct whatever those rows contain:
    // total = paid bike + protection + accessories.
    var addons = Math.max(0, view.total - view.paidPrice);
    var dueToday = Math.min(cfg.depositAmount + addons, view.total);
    var dueLater = Math.max(0, view.total - dueToday);

    // With a pay-in-full saving, show the struck original beside the price
    // the customer will actually pay once the code applies at checkout.
    var fullSaving = cfg.fullSaving > 0 && view.total > cfg.fullSaving ? cfg.fullSaving : 0;
    setText('bogo-deposit-full-was', fullSaving ? money(view.total) : '');
    setText('bogo-deposit-full-amount', money(view.total - fullSaving) + ' today');
    setText('bogo-deposit-deposit-amount', money(cfg.depositAmount) + ' today');

    // The saving comes from a Shopify discount CODE applied at checkout, so the
    // cart total above deliberately still shows the undiscounted figure. Saying
    // "at checkout" is what keeps the two numbers honest.
    setText(
      'bogo-deposit-full-save',
      cfg.fullSaving > 0 && cfg.fullSavingText
        ? cfg.fullSavingText.split('[[saving]]').join(money(cfg.fullSaving))
        : ''
    );

    var buttons = root.querySelectorAll('.bogo-deposit-option');
    for (var i = 0; i < buttons.length; i++) {
      var selected = buttons[i].getAttribute('data-mode') === mode;
      buttons[i].classList.toggle('is-selected', selected);
      buttons[i].setAttribute('aria-checked', selected ? 'true' : 'false');
      buttons[i].setAttribute('tabindex', selected ? '0' : '-1');
    }

    renderNote(view);

    var due = document.getElementById('bogo-deposit-due');
    if (due) due.hidden = !isDeposit;

    // Itemised so the $500 in the option card and the larger "due today"
    // figure reconcile: add-ons ship immediately and are charged in full.
    setText('bogo-deposit-deposit-line', money(Math.min(cfg.depositAmount, view.total)));
    var addonsRow = document.getElementById('bogo-deposit-addons-row');
    if (addonsRow) addonsRow.hidden = !(addons > 0);
    setText('bogo-deposit-addons', money(addons));

    setText('bogo-deposit-today', money(dueToday));
    setText('bogo-deposit-later', money(dueLater));
    setText('bogo-deposit-hint', addons > 0 ? cfg.hintAddons : cfg.hint);

    applyCta(isDeposit);
  }

  function renderNote(view) {
    var note = document.getElementById('bogo-deposit-note');
    if (!note) return;

    if (!eligible || !cfg.noteTemplate) {
      note.textContent = '';
      return;
    }

    // Name the bike the customer is actually waiting on. Paid bike first,
    // since that is the line carrying the deposit.
    var name = isPreorder(view.paidEta) ? view.paidName : view.freeName;
    var eta = isPreorder(view.paidEta) ? view.paidEta : view.freeEta;

    note.textContent = cfg.noteTemplate
      .split('[[bike]]')
      .join(name)
      .split('[[eta]]')
      .join(eta);
  }

  function applyCta(isDeposit) {
    if (!ctaBtn || ctaBtn.disabled) return;

    var wanted = isDeposit
      ? cfg.ctaDeposit.split('[[amount]]').join(money(cfg.depositAmount))
      : ctaOriginal;

    // Never blank the button: if we failed to capture a label, leave it alone.
    if (!wanted.trim()) return;
    if (ctaBtn.textContent.trim() !== wanted.trim()) ctaBtn.textContent = wanted;
  }

  // ------------------------------------------- steps 1 & 2 pre-order badge

  var BADGE_CLASS = 'bogo-deposit-preorder-badge';
  var badgeObservers = [];

  /**
   * Two different ETA rows carry a pre-order date on steps 1 and 2:
   *   .product-form__shipping   the bike card in the grid  (bundle-item-card)
   *   .bogo-qv-shipping         the quick-view modal       (bogo-quick-view)
   * Both put `available` / `caution` on the row itself, so one code path
   * handles them once the surrounding card is located.
   */
  var ANCHOR_SELECTOR = '.bogo-qv-shipping, .product-form__shipping';

  function anchorScope(wrap) {
    if (!wrap.closest) return null;
    return wrap.closest('.bogo-qv-backdrop') || wrap.closest('product-card');
  }

  function anchorEta(wrap) {
    var el = wrap.querySelector('.bogo-qv-shipping-time, .shipping-info_shipping-time');
    return el ? el.textContent.trim() : '';
  }

  function wrapIsPreorder(wrap) {
    if (wrap.classList.contains('caution')) return true;
    if (wrap.classList.contains('available')) return false;

    var shown = anchorEta(wrap);
    if (!shown) return false;

    var fallback = (wrap.getAttribute('data-default-timeline') || '').trim();
    return fallback ? shown !== fallback : isPreorder(shown);
  }

  function wrapPlanId(wrap) {
    var scope = anchorScope(wrap);
    if (!scope) return null;

    var handle = scope.getAttribute && scope.getAttribute('data-product-handle');
    if (!handle) {
      var btn = scope.querySelector('.bogo-qv-select-btn[data-product-handle]');
      handle = btn ? btn.getAttribute('data-product-handle') : '';
    }
    return handle ? getPlan(handle) : null;
  }

  /**
   * Re-uses the page's own currency formatting by lifting the prefix/suffix
   * off the price string, so CA$ stays CA$ without this file having to know
   * the shop's money_format.
   */
  function formatLike(sample, value) {
    var str = String(sample);
    var prefix = (str.match(/^[^0-9]*/) || [''])[0];
    var suffix = (str.match(/[^0-9.,]*$/) || [''])[0];
    var hasCents = Math.round(value * 100) % 100 !== 0;
    return prefix + value.toLocaleString('en-CA', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2
    }) + suffix;
  }

  /** The card's own price string, so both amounts can be formatted like it. */
  function wrapPriceText(wrap) {
    var scope = anchorScope(wrap);
    if (!scope) return null;

    var el = scope.querySelector('.bogo-qv-price-current, .product-item__price-list .price');
    if (!el) return null;

    // The grid card prefixes the money with a visually-hidden "Sale price"
    // label; including it would poison the currency prefix detection.
    var clone = el.cloneNode(true);
    var hidden = clone.querySelectorAll('.visually-hidden');
    for (var i = 0; i < hidden.length; i++) {
      if (hidden[i].parentNode) hidden[i].parentNode.removeChild(hidden[i]);
    }

    var text = clone.textContent.trim();
    return text || null;
  }

  function syncBadge(wrap) {
    // The badge is inserted BEFORE the shipping row, so the ETA, spec box and
    // CTA below it start at the same offset on every card.
    var prev = wrap.previousElementSibling;
    var badge = prev && prev.classList.contains(BADGE_CLASS) ? prev : null;

    var preorder = wrapIsPreorder(wrap);
    var eta = anchorEta(wrap);

    // Must be a RESOLVED number. wrapPlanId returns undefined while the
    // /products/<handle>.js lookup is still in flight, and treating that as
    // eligible renders the badge optimistically — a bike with no purchase
    // option would flash "Pre-Order with $500 Deposit" and then lose it.
    var wrapPlan = preorder ? wrapPlanId(wrap) : null;

    var variant = null;
    if (cfg.showBadge && cfg.badgeTitle && preorder && typeof wrapPlan === 'number') {
      variant = 'preorder';
    } else if (cfg.showInstock && cfg.instockTitle && !preorder && eta) {
      // Requires a real ETA: an empty shipping row is unknown, not in stock.
      variant = 'instock';
    }

    if (!variant) {
      if (badge && badge.parentNode) badge.parentNode.removeChild(badge);
      return;
    }

    if (!badge) {
      badge = document.createElement('div');
      badge.innerHTML =
        '<div class="bogo-deposit-preorder-title"></div>' +
        '<div class="bogo-deposit-preorder-body"></div>';
      wrap.insertAdjacentElement('beforebegin', badge);
    }

    var className = BADGE_CLASS + (variant === 'instock' ? ' is-instock' : '');

    var title;
    var body;

    if (variant === 'instock') {
      title = cfg.instockTitle.split('[[eta]]').join(eta);
      body = cfg.instockBody.split('[[eta]]').join(eta);
    } else {
      // Format both amounts like the card's own price so "CA$" stays "CA$".
      var priceText = wrapPriceText(wrap);
      var price = priceText ? parseMoney(priceText) : 0;
      var deposit = priceText ? formatLike(priceText, cfg.depositAmount) : money(cfg.depositAmount);

      title = cfg.badgeTitle.split('[[amount]]').join(deposit);

      var balance = price > cfg.depositAmount
        ? formatLike(priceText, price - cfg.depositAmount)
        : null;

      // Only the [[balance]] token depends on a readable price. Body text that
      // does not use it must still render when the price cannot be parsed.
      var needsBalance = cfg.badgeBody.indexOf('[[balance]]') !== -1;
      body = '';
      if (cfg.badgeBody && (!needsBalance || balance)) {
        body = cfg.badgeBody
          .split('[[balance]]').join(balance || '')
          .split('[[eta]]').join(eta);
      }
    }

    var titleEl = badge.querySelector('.bogo-deposit-preorder-title');
    if (titleEl && titleEl.textContent !== title) titleEl.textContent = title;

    var bodyEl = badge.querySelector('.bogo-deposit-preorder-body');
    if (bodyEl && bodyEl.textContent !== body) bodyEl.textContent = body;

    // With no body the badge collapses to a single band. Safe for alignment:
    // the badge sits above the shipping row, and everything below that row is
    // identical across cards, so the row itself stays level regardless.
    if (!body) className += ' is-titleonly';
    if (badge.className !== className) badge.className = className;
  }

  function syncBadges() {
    if (!cfg) return;
    var wraps = document.querySelectorAll(ANCHOR_SELECTOR);
    for (var i = 0; i < wraps.length; i++) syncBadge(wraps[i]);
  }

  function removeBadges() {
    var badges = document.querySelectorAll('.' + BADGE_CLASS);
    for (var i = 0; i < badges.length; i++) {
      if (badges[i].parentNode) badges[i].parentNode.removeChild(badges[i]);
    }
  }

  /** One observer per ETA row — catches variant changes from the dropdown,
      the colour swatches, and the card/modal being re-rendered. */
  function startBadgeObservers() {
    if (typeof MutationObserver !== 'function') return;
    var wraps = document.querySelectorAll(ANCHOR_SELECTOR);
    for (var i = 0; i < wraps.length; i++) {
      var observer = new MutationObserver(function () { schedule(); });
      observer.observe(wraps[i], {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class']
      });
      badgeObservers.push(observer);
    }
  }

  function stopBadgeObservers() {
    for (var i = 0; i < badgeObservers.length; i++) badgeObservers[i].disconnect();
    badgeObservers = [];
  }

  // ---------------------------------------------------------------- events

  function setMode(next, moveFocus) {
    if (!eligible && next === DEPOSIT_MODE) return;
    mode = next === DEPOSIT_MODE ? DEPOSIT_MODE : FULL_MODE;
    evaluate();

    if (!moveFocus || !root) return;
    var active = root.querySelector('.bogo-deposit-option[data-mode="' + mode + '"]');
    if (active) active.focus();
  }

  function onClick(event) {
    var button = event.target.closest ? event.target.closest('.bogo-deposit-option') : null;
    if (!button || !root.contains(button)) return;
    guard(function () {
      setMode(button.getAttribute('data-mode'), false);
    });
  }

  function onKeydown(event) {
    var key = event.key;
    var isArrow =
      key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown';
    if (!isArrow && key !== ' ' && key !== 'Enter') return;

    var button = event.target.closest ? event.target.closest('.bogo-deposit-option') : null;
    if (!button || !root.contains(button)) return;

    event.preventDefault();
    guard(function () {
      if (isArrow) {
        setMode(mode === FULL_MODE ? DEPOSIT_MODE : FULL_MODE, true);
      } else {
        setMode(button.getAttribute('data-mode'), false);
      }
    });
  }

  /** Step 4 summary and the step 1/2 badges are refreshed together. */
  function tick() {
    evaluate();
    syncBadges();
    primeCodeProducts();
  }

  function schedule() {
    if (frame !== null) return;
    var raf = window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); };
    frame = raf(function () {
      frame = null;
      guard(tick);
    });
  }

  /**
   * bogo-builder.js repaints step 4 from several entry points, so watch the
   * card instead of trying to hook each one. Mutations inside our own block
   * are ignored, which keeps render() from re-triggering the observer.
   */
  function startObserver() {
    var step4 = document.getElementById('bogo-step-4');
    if (!step4 || typeof MutationObserver !== 'function') {
      warnOnce('noobserver', 'step-4 container not observable — updates may lag');
      return;
    }

    observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (root && root.contains(records[i].target)) continue;
        schedule();
        return;
      }
    });

    observer.observe(step4, { childList: true, subtree: true, characterData: true });
  }

  // ------------------------------------------------------- mount / teardown

  /**
   * Roots are located by CLASS, never by `#bogo-deposit-root`. While the theme
   * editor re-renders the section there can briefly be two elements carrying
   * that id, and a `#id` query is only specified to return the first one in
   * the document — which may be the stale copy we are trying to replace.
   */
  function findRoot() {
    var nodes = document.querySelectorAll('.bogo-deposit-root');
    var mounted = null;
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute(MOUNT_ATTR) !== 'true') return nodes[i]; // freshly rendered
      if (!mounted) mounted = nodes[i];
    }
    return mounted;
  }

  function removeMountedRoots(except) {
    var nodes = document.querySelectorAll('.bogo-deposit-root[' + MOUNT_ATTR + '="true"]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i] !== except && nodes[i].parentNode) {
        nodes[i].parentNode.removeChild(nodes[i]);
      }
    }
  }

  function mount() {
    var candidate = findRoot();

    // The checkout section can be re-rendered from under us, taking our
    // injected block with it. Rebuild from the snapshot rather than silently
    // losing the feature until the next full page load.
    if (!candidate && template) candidate = template.cloneNode(true);
    if (!candidate) return;
    if (candidate === root && candidate.isConnected) return; // already mounted

    var totals = document.querySelector('.bogo-checkout-totals');
    if (!totals) {
      warnOnce('nototals', '.bogo-checkout-totals not found — deposit option not shown');
      return;
    }

    cfg = readConfig(candidate);
    if (!template) {
      template = candidate.cloneNode(true);
      template.removeAttribute(MOUNT_ATTR);
    }

    // Idempotence: exactly one injected block, always.
    removeMountedRoots(candidate);

    ctaBtn = document.querySelector('.bogo-checkout-cta-btn');
    if (ctaBtn) {
      ctaOriginal = ctaBtn.getAttribute('data-bogo-deposit-cta') || ctaBtn.textContent.trim();
      ctaBtn.setAttribute('data-bogo-deposit-cta', ctaOriginal);
    }

    candidate.hidden = true;
    totals.insertAdjacentElement('afterend', candidate);
    candidate.setAttribute(MOUNT_ATTR, 'true');
    root = candidate;

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeydown);
    startObserver();
    startBadgeObservers();
    tick();
  }

  function teardown() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    stopBadgeObservers();
    removeBadges();
    if (root) {
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKeydown);
      root.hidden = true;
      if (root.parentNode) root.parentNode.removeChild(root);
      root.removeAttribute(MOUNT_ATTR);
    }
    removeMountedRoots(null);
    if (ctaBtn && !ctaBtn.disabled && ctaOriginal) ctaBtn.textContent = ctaOriginal;

    root = null;
    eligible = false;
    planId = null;
    mode = FULL_MODE;
  }

  // ------------------------------------------------------------ public API

  window.BogoDeposit = {
    isActive: function () {
      return !!(root && root.isConnected && eligible && mode === DEPOSIT_MODE && planId !== null);
    },
    getPlanId: function () {
      return window.BogoDeposit.isActive() ? planId : null;
    },
    getMode: function () {
      return eligible ? mode : FULL_MODE;
    },

    /**
     * Diagnostic snapshot. Everything the checkout redirect depends on, so a
     * wrong or missing discount code can be read off the page instead of
     * guessed at.
     */
    debug: function () {
      var freeName = textOf('checkout-bike2-name');
      return {
        mounted: !!(root && root.isConnected),
        mode: mode,
        eligible: eligible,
        planId: planId,
        paidHandle: paidHandle,
        selectedHandles: selectedHandles(),
        freeHandle: freeHandle(),
        paidName: textOf('checkout-bike1-name'),
        freeName: freeName,
        codeMap: cfg ? cfg.depositCodes : null,
        freeVariantTitle: textOf('checkout-bike2-variant'),
        productTitles: titleByHandle,
        freeBikeCode: cfg ? freeBikeCode() : null,
        fullDiscountCode: cfg ? cfg.discountCode : null,
        preorderPresent: preorderPresent,
        fullCodePreorderOnly: cfg ? cfg.fullCodePreorderOnly : null,
        checkoutUrl: window.BogoDeposit.getCheckoutUrl('/checkout')
      };
    },

    /**
     * Called by bogo-builder.js instead of hardcoding '/checkout'.
     *
     * Appends the pay-in-full discount code for any order that is NOT a
     * deposit — that includes the all-in-stock case where no choice was ever
     * offered, since those customers are paying in full too. Deposit orders
     * never get it; that withholding is the whole point of the incentive.
     *
     * Returns `base` untouched on any problem, so a bad setting can never
     * strand a customer on a broken checkout URL.
     */
    getCheckoutUrl: function (base) {
      var url = base || '/checkout';
      try {
        if (!cfg) return url;

        // Deposit path: the per-bike BOGO deposit code, matched on the PAID
        // bike — the line that carries the plan. Bikes with no mapping get
        // no code rather than someone else's.
        var code;
        if (window.BogoDeposit.isActive()) {
          code = freeBikeCode();
        } else if (cfg.fullCodePreorderOnly && !preorderPresent) {
          // Pay-in-full incentive exists to steer pre-order buyers away from
          // a deposit. An all-in-stock pair has nothing to be steered from,
          // so it must not receive the discount.
          code = '';
        } else {
          code = cfg.discountCode;
        }

        if (!code) return url;
        return url + (url.indexOf('?') === -1 ? '?' : '&') +
          'discount=' + encodeURIComponent(code);
      } catch (err) {
        return url;
      }
    }
  };

  // ---------------------------------------------------------------- startup

  function boot() {
    guard(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: the section can be re-rendered or removed without a reload.
  // Re-mount for our own section AND for any section that brings a new
  // .bogo-checkout-totals with it, since that replaces our injection point.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.classList) return;

    var isOurs = target.classList.contains('bogo-deposit-option-section');
    var bringsTotals = !!(target.querySelector && target.querySelector('.bogo-checkout-totals'));
    if (!isOurs && !bringsTotals) return;

    guard(function () {
      teardown();
      mount();
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.classList) return;
    if (!target.classList.contains('bogo-deposit-option-section')) return;
    guard(function () {
      teardown();
      template = null; // feature genuinely removed — do not resurrect it
    });
  });
})();
